import React, { useState, useEffect } from 'react';
import usePageTitle from '../hooks/usePageTitle';
import { useNavigate } from 'react-router-dom';
import { Save, ArrowLeft, Package, Building2, MapPin, Plus, Trash2, User, Wrench, Hash, Loader, Lightbulb, CheckCircle, AlertCircle } from 'lucide-react';
import { API_URL } from '../config/api';
import apiService from '../services/apiService';
import SearchableDropdown from '../components/SearchableDropdown';
import toast from '../utils/toast';
import ConfirmationModal from '../components/ConfirmationModal';

const AddAsset = () => {
  usePageTitle('Add Asset');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Modal states
  const [showCreateConfirmation, setShowCreateConfirmation] = useState(false);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  
  // State tracking for intelligent error handling
  const [userHasInteracted, setUserHasInteracted] = useState(false);
  const [projectSearchError, setProjectSearchError] = useState(null);
  
  // Auto-populated project data
  const [projectData, setProjectData] = useState({
    project_reference_num: '',
    customer_name: '',
    customer_reference_number: '',
    project_title: ''
  });
  
  // Dropdowns data
  const [availableProjects, setAvailableProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [projectSearchTerm, setProjectSearchTerm] = useState('');
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const [branches, setBranches] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [categories, setCategories] = useState([]);
  const [models, setModels] = useState([]);
  const [peripheralTypes, setPeripheralTypes] = useState([]);

  // Modal states for adding new options dynamically
  const [showAddModal, setShowAddModal] = useState(false);
  const [modalType, setModalType] = useState(''); // 'category', 'model', 'peripheral', 'windows', 'office', 'antivirus', 'software'
  const [newOptionValue, setNewOptionValue] = useState('');
  const [addingOption, setAddingOption] = useState(false);

  // Dropdown options for new fields
  const [windowsOptions, setWindowsOptions] = useState([]);
  const [officeOptions, setOfficeOptions] = useState([]);
  const [antivirusOptions, setAntivirusOptions] = useState([]);
  const [softwareOptions, setSoftwareOptions] = useState([]);
  const [projectAntivirusName, setProjectAntivirusName] = useState('');
  const [newSoftwarePrice, setNewSoftwarePrice] = useState('');
  
  // Form data
  const [selectedBranch, setSelectedBranch] = useState('');
  
  // Multiple software selection (similar to branches in AddProject)
  const [selectedSoftware, setSelectedSoftware] = useState([]);
  const [softwareInput, setSoftwareInput] = useState('');
  
  // Main asset form data
  const [asset, setAsset] = useState({
    serial_number: '',
    tag_id: '',
    item_name: '',
    category: '',
    model: '',
    status: 'Active',
    recipient_name: '',
    department_name: '',
    position: '',
    windows: '',
    microsoft_office: '',
    antivirus: '',
    monthly_prices: ''
  });

  // Peripheral data
  const [peripherals, setPeripherals] = useState([{
    peripheral_name: '',
    serial_code_name: '',
    condition: 'Good',
    remarks: ''
  }]);

  // Auto-generated fields (read-only)
  const [autoGenerated, setAutoGenerated] = useState({
    asset_id: 'Will be auto-generated',
    pmid: 'Will be auto-generated',
    inventory_id: 'Will be auto-generated'
  });

  // Fetch initial data on mount
  useEffect(() => {
    fetchAvailableProjects();
    fetchRecipients();
    fetchCategories();
    fetchModels();
    fetchPeripheralTypes();
    fetchWindowsOptions();
    fetchOfficeOptions();
    fetchAntivirusOptions();
    fetchSoftwareOptions();
  }, []);

  // Filter projects based on search term
  useEffect(() => {
    if (projectSearchTerm.trim() === '') {
      setFilteredProjects(availableProjects);
    } else {
      const filtered = availableProjects.filter(project => 
        project.Project_Ref_Number.toLowerCase().includes(projectSearchTerm.toLowerCase()) ||
        project.Project_Title.toLowerCase().includes(projectSearchTerm.toLowerCase()) ||
        project.Customer_Name?.toLowerCase().includes(projectSearchTerm.toLowerCase())
      );
      setFilteredProjects(filtered);
    }
  }, [projectSearchTerm, availableProjects]);

  // Auto-populate project data when project reference number changes
  useEffect(() => {
    if (projectData.project_reference_num.trim()) {
      // Only fetch if we don't already have the customer name (meaning data wasn't populated from dropdown)
      if (!projectData.customer_name) {
        fetchProjectByReference(projectData.project_reference_num);
      }
    } else {
      // Clear dependent data when project ref is cleared
      setBranches([]);
      setSelectedBranch('');
      setProjectData(prev => ({
        project_reference_num: prev.project_reference_num,
        customer_name: '',
        customer_reference_number: '',
        project_title: ''
      }));
    }
  }, [projectData.project_reference_num]);

  // Fetch branches when customer name is populated
  useEffect(() => {
    if (projectData.customer_name) {
      fetchBranchesByCustomer(projectData.customer_name);
    }
  }, [projectData.customer_name]);

  const fetchAvailableProjects = async () => {
    try {
      setLoading(true);
      // Fetch all projects without pagination (use high limit to get all)
      const response = await apiService.getAllProjects(1, 1000);
      console.log('Available projects response:', response);
      
      // Handle both paginated and direct array responses
      let projects = [];
      if (response.data) {
        // If it's a paginated response with data property
        projects = Array.isArray(response.data) ? response.data : [];
      } else if (Array.isArray(response)) {
        // If it's a direct array
        projects = response;
      }
      
      console.log(`✅ Loaded ${projects.length} projects`);
      setAvailableProjects(projects);
      setFilteredProjects(projects);
      setProjectSearchError(null); // Clear search-specific errors
    } catch (err) {
      console.error('Error fetching available projects:', err);
      
      // Only set error if user has actually interacted with the search
      if (userHasInteracted) {
        setProjectSearchError('Failed to load available projects');
      }
      
      // Set mock data as fallback (always available)
      const mockProjects = [
        {
          Project_ID: 1,
          Project_Ref_Number: 'QT240000000015729',
          Project_Title: 'PERKHIDMATAN SEWAAN PERKAKASAN ICT,PENYELENGGARAAN KOMPREHENSIF DAN BANTUAN SOKONGAN BAGI TEMPOH 37 BULAN DI AGENSI PENGURUSAN BENCANA NEGARA (NADMA)',
          Customer_Name: 'NADMA'
        },
        {
          Project_ID: 6,
          Project_Ref_Number: 'QT240000000029575',
          Project_Title: 'PERKHIDMATAN SEWAAN PERALATAN ICT DI INSTITUT LATIHAN ISLAM MALAYSIA (ILIM)',
          Customer_Name: 'ILIM'
        },
        {
          Project_ID: 8,
          Project_Ref_Number: 'QT244567890987',
          Project_Title: 'PERKHIDMATAN SEWAAN PERALATAN ICT DI MAHKAMAH SYARIAH WILAYAH PERSEKUTUAN KUALA LUMPUR',
          Customer_Name: 'MSWP'
        }
      ];
      console.log('⚠️ Using fallback mock projects');
      setAvailableProjects(mockProjects);
      setFilteredProjects(mockProjects);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectByReference = async (projectRefNum) => {
    try {
      setLoading(true);
      const response = await apiService.getProjectByReference(projectRefNum);
      
      if (response.data) {
        
        setProjectData(prev => ({
          ...prev,
          customer_name: response.data.customer_name,
          customer_reference_number: response.data.customer_reference_number,
          project_title: response.data.project_title
        }));
        
        // Store project antivirus name for dropdown
        const antivirusValue = response.data.antivirus;
        
        // Check if antivirus value is a valid non-empty string
        const isValidAntivirus = antivirusValue && 
                                  typeof antivirusValue === 'string' &&
                                  antivirusValue !== 'None' && 
                                  antivirusValue !== 'null' && 
                                  antivirusValue.trim() !== '';
        
        if (isValidAntivirus) {
          const trimmedValue = antivirusValue.trim();
          setProjectAntivirusName(trimmedValue);
          // Don't auto-select, let user choose from dropdown
          setAsset(prev => ({
            ...prev,
            antivirus: '' // Reset to force user selection
          }));
        } else {
          setProjectAntivirusName('');
        }
        
        setError(null);
        
        // Automatically fetch branches when customer is found
        if (response.data.customer_name) {
          console.log('🔄 Auto-fetching branches for customer:', response.data.customer_name);
          await fetchBranchesByCustomer(response.data.customer_name);
        }
      } else {
        console.warn('No project found for reference:', projectRefNum);
        setError(`No project found with reference number "${projectRefNum}"`);
      }
    } catch (err) {
      console.error('Error fetching project:', err);
      setError(`Failed to load project "${projectRefNum}". ${err.message || 'Please check the reference number and try again.'}`);
      // Clear dependent fields
      setProjectData(prev => ({
        ...prev,
        customer_name: '',
        customer_reference_number: '',
        project_title: ''
      }));
      setBranches([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchBranchesByCustomer = async (customerName) => {
    try {
      console.log('🔄 Fetching branches for customer:', customerName);
      console.log('🔍 Customer name type:', typeof customerName);
      console.log('🔍 Customer name exact value:', JSON.stringify(customerName));
      setLoading(true);
      
      const response = await apiService.getBranchesByCustomer(customerName);
      console.log('✅ Branch response received:', response);
      
      const branches = response.data || [];
      setBranches(branches);
      
      if (branches.length === 0) {
        console.warn('⚠️ No branches found for customer:', customerName);
        
        // Provide fallback branches if none found
        const fallbackBranches = [
          {
            Customer_ID: 'temp_1',
            Customer_Ref_Number: 'TEMP-001',
            Customer_Name: customerName,
            Branch: 'Head Office'
          },
          {
            Customer_ID: 'temp_2',
            Customer_Ref_Number: 'TEMP-002',
            Customer_Name: customerName,
            Branch: 'Branch Office'
          }
        ];
        
        setBranches(fallbackBranches);
        setError(`Using default branches for "${customerName}". No specific branches found in database.`);
        
        console.log('📋 Using fallback branches:', fallbackBranches);
      } else {
        console.log(`✅ Found ${branches.length} branches for ${customerName}`);
        setError(null); // Clear any previous errors
      }
      
    } catch (err) {
      console.error('❌ Error fetching branches:', err);
      console.error('❌ Error details:', {
        message: err.message,
        stack: err.stack,
        customerName
      });
      
      // Provide fallback branches on error
      const fallbackBranches = [
        {
          Customer_ID: 'fallback_1',
          Customer_Ref_Number: 'FB-001',
          Customer_Name: customerName,
          Branch: 'Main Office'
        },
        {
          Customer_ID: 'fallback_2',
          Customer_Ref_Number: 'FB-002',
          Customer_Name: customerName,
          Branch: 'Secondary Office'
        }
      ];
      
      setBranches(fallbackBranches);
      // Don't show error to user, just log it - fallback branches work fine
      console.warn(`⚠️ Branch loading failed for "${customerName}". Using default options. (${err.message || 'Network error'})`);
      
      console.log('📋 Using fallback branches due to error:', fallbackBranches);
    } finally {
      setLoading(false);
    }
  };



  const fetchRecipients = async () => {
    try {
      const response = await apiService.getRecipients();
      setRecipients(response.data || []);
    } catch (err) {
      console.error('Error fetching recipients:', err);
      setRecipients([]);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await apiService.getCategories();
      const categoriesData = response.data || [];
      
      // Check if backend is returning fallback data
      if (response.fallback) {
        console.warn('⚠️ Backend returned FALLBACK categories (database error)');
      }
      
      setCategories(categoriesData);
      return categoriesData;
    } catch (err) {
      console.error('Error fetching categories:', err);
      setCategories([]);
      return [];
    }
  };

  const fetchModels = async () => {
    try {
      const response = await apiService.getModels();
      const modelsData = response.data || [];
      setModels(modelsData);
      return modelsData;
    } catch (err) {
      console.error('Error fetching models:', err);
      setModels([]);
      return [];
    }
  };

  const fetchPeripheralTypes = async () => {
    try {
      const response = await apiService.getPeripheralTypes();
      const typesData = response.data || [];
      setPeripheralTypes(typesData);
      return typesData;
    } catch (err) {
      console.error('Error fetching peripheral types:', err);
      setPeripheralTypes([]);
      return [];
    }
  };

  const fetchWindowsOptions = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/options/windows`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const result = await response.json();
      const optionsData = result.data || [];
      setWindowsOptions(optionsData);
      return optionsData;
    } catch (err) {
      console.error('Error fetching Windows options:', err);
      setWindowsOptions([]);
      return [];
    }
  };

  const fetchOfficeOptions = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/options/office`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const result = await response.json();
      const optionsData = result.data || [];
      setOfficeOptions(optionsData);
      return optionsData;
    } catch (err) {
      console.error('Error fetching Office options:', err);
      setOfficeOptions([]);
      return [];
    }
  };

  const fetchAntivirusOptions = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/options/antivirus`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const result = await response.json();
      const optionsData = result.data || [];
      setAntivirusOptions(optionsData);
      return optionsData;
    } catch (err) {
      console.error('Error fetching Antivirus options:', err);
      setAntivirusOptions([]);
      return [];
    }
  };

  const fetchSoftwareOptions = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/options/software`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const result = await response.json();
      const optionsData = result.data || [];
      setSoftwareOptions(optionsData);
      return optionsData;
    } catch (err) {
      console.error('Error fetching Software options:', err);
      setSoftwareOptions([]);
      return [];
    }
  };

  // Modal handlers for adding new options
  const handleOpenAddModal = (type) => {
    setModalType(type);
    setNewOptionValue('');
    setNewSoftwarePrice('');
    setShowAddModal(true);
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setModalType('');
    setNewOptionValue('');
    setNewSoftwarePrice('');
    setAddingOption(false);
  };

  const handleAddNewOption = async () => {
    if (!newOptionValue.trim()) {
      toast.error('Please enter a value');
      return;
    }

    setAddingOption(true);
    try {
      let endpoint = '';
      let updateStateFunction = null;
      let autoSelectField = '';
      let requestBody = {};
      let selectedPrice = null;

      // Determine endpoint and state update function based on modalType
      if (modalType === 'category') {
        endpoint = '/categories';
        updateStateFunction = setCategories;
        autoSelectField = 'category';
        requestBody = { name: newOptionValue.trim() };
      } else if (modalType === 'model') {
        endpoint = '/models';
        updateStateFunction = setModels;
        autoSelectField = 'model';
        requestBody = { name: newOptionValue.trim() };
      } else if (modalType === 'peripheral') {
        endpoint = '/peripherals/types';
        updateStateFunction = setPeripheralTypes;
        autoSelectField = 'peripheral_name';
        requestBody = { name: newOptionValue.trim() };
      } else if (modalType === 'windows') {
        endpoint = '/options/windows';
        updateStateFunction = setWindowsOptions;
        autoSelectField = 'windows';
        requestBody = { value: newOptionValue.trim() };
      } else if (modalType === 'office') {
        endpoint = '/options/office';
        updateStateFunction = setOfficeOptions;
        autoSelectField = 'microsoft_office';
        requestBody = { value: newOptionValue.trim() };
      } else if (modalType === 'antivirus') {
        endpoint = '/options/antivirus';
        updateStateFunction = setAntivirusOptions;
        autoSelectField = 'antivirus';
        requestBody = { value: newOptionValue.trim() };
      } else if (modalType === 'software') {
        endpoint = '/options/software';
        updateStateFunction = setSoftwareOptions;
        autoSelectField = 'software';
        requestBody = { 
          value: newOptionValue.trim(),
          price: newSoftwarePrice ? Number(newSoftwarePrice) : null
        };
      }

      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody),
      });

      const result = await response.json();

      // Handle both success (201) and conflict (409 - already exists)
      if ((response.ok && result.success) || response.status === 409) {
        let selectedValue = newOptionValue.trim();
        
        // If 409 conflict, refresh dropdown to get the existing item from database
        if (response.status === 409) {
          console.log(`✅ ${modalType} "${selectedValue}" already exists, refreshing dropdown...`);
          
          // Refresh the appropriate dropdown list and get the returned data
          let refreshedData = [];
          if (modalType === 'category') {
            refreshedData = await fetchCategories();
            console.log('📋 Categories state after fetch:', refreshedData);
          } else if (modalType === 'model') {
            refreshedData = await fetchModels();
            console.log('📋 Models state after fetch:', refreshedData);
          } else if (modalType === 'peripheral') {
            refreshedData = await fetchPeripheralTypes();
            console.log('📋 Peripheral types state after fetch:', refreshedData);
          } else if (modalType === 'windows') {
            refreshedData = await fetchWindowsOptions();
            console.log('📋 Windows options state after fetch:', refreshedData);
          } else if (modalType === 'office') {
            refreshedData = await fetchOfficeOptions();
            console.log('📋 Office options state after fetch:', refreshedData);
          } else if (modalType === 'antivirus') {
            refreshedData = await fetchAntivirusOptions();
            console.log('📋 Antivirus options state after fetch:', refreshedData);
          } else if (modalType === 'software') {
            refreshedData = await fetchSoftwareOptions();
            console.log('📋 Software options state after fetch:', refreshedData);
          }
          
          console.log(`✅ Refreshed ${modalType} dropdown with ${refreshedData.length} items`);
          
          // Wait a bit for state to fully update
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // For object arrays, find the exact matching item
          if (['category', 'model', 'peripheral'].includes(modalType)) {
            const matchingItem = refreshedData.find(item => {
              const itemName = item.Category || item.Model_Name || item.Model || item.Peripheral_Type_Name || item.name || '';
              return itemName.toLowerCase() === selectedValue.toLowerCase();
            });
            
            if (matchingItem) {
              selectedValue = matchingItem.Category || matchingItem.Model_Name || matchingItem.Model || matchingItem.Peripheral_Type_Name || matchingItem.name;
              console.log(`✅ Found matching ${modalType}:`, selectedValue);
            }
          } else if (modalType === 'software') {
            const matchingItem = refreshedData.find(item => {
              const itemName = item.Software_Name || item.name || '';
              return itemName.toLowerCase() === selectedValue.toLowerCase();
            });

            if (matchingItem) {
              selectedValue = matchingItem.Software_Name || matchingItem.name;
              selectedPrice = matchingItem.Price ?? matchingItem.price ?? null;
              console.log(`✅ Found matching software:`, selectedValue, 'price:', selectedPrice);
            }
          }
        } else {
          // Success case - add new option to dropdown
          const newOption = result.data;
          
          // For simple string arrays (windows, office, antivirus, software)
          if (['windows', 'office', 'antivirus'].includes(modalType)) {
            updateStateFunction(prev => [...prev, newOption]);
            selectedValue = newOption;
          } else if (modalType === 'software') {
            const softwareEntry = newOption || { Software_Name: newOptionValue.trim(), Price: newSoftwarePrice ? Number(newSoftwarePrice) : null };
            updateStateFunction(prev => [...prev, softwareEntry]);
            selectedValue = softwareEntry.Software_Name || softwareEntry.name || newOptionValue.trim();
            selectedPrice = softwareEntry.Price ?? softwareEntry.price ?? null;
          } else {
            // For object arrays (category, model, peripheral)
            updateStateFunction(prev => [...prev, newOption]);
            
            if (modalType === 'category') {
              selectedValue = newOption.Category || newOption.name;
            } else if (modalType === 'model') {
              selectedValue = newOption.Model_Name || newOption.Model || newOption.name;
            } else if (modalType === 'peripheral') {
              selectedValue = newOption.Peripheral_Type_Name || newOption.name;
            }
          }
          
          console.log(`✅ Added new ${modalType}:`, selectedValue);
        }

        // Auto-select the value in the form
        setAsset(prevAsset => ({
          ...prevAsset,
          [autoSelectField]: selectedValue
        }));
        
        console.log(`✅ Auto-selected ${autoSelectField}:`, selectedValue);

        handleCloseAddModal();
      } else {
        toast.error(result.error || 'Failed to add new option');
        setAddingOption(false);
      }
    } catch (error) {
      console.error('Error adding new option:', error);
      toast.error('Failed to add new option. Please try again.');
      setAddingOption(false);
    }
  };

  const handleSubmitClick = (e) => {
    e.preventDefault();
    
    // Validate form before showing confirmation
    if (!projectData.project_reference_num.trim()) {
      setError('Please enter a project reference number');
      return;
    }
    
    if (!selectedBranch) {
      setError('Please select a branch');
      return;
    }
    
    if (!asset.serial_number.trim() || !asset.tag_id.trim() || !asset.item_name.trim()) {
      setError('Please fill in all required asset fields');
      return;
    }

    // Validate peripheral data
    const peripheralErrors = validatePeripherals();
    if (peripheralErrors.length > 0) {
      setError(`Peripheral validation errors:\n${peripheralErrors.join('\n')}`);
      return;
    }
    
    // Clear any previous errors and show confirmation
    setError(null);
    setShowCreateConfirmation(true);
  };

  const handleConfirmCreate = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Prepare complete asset data
      const completeAssetData = {
        // Project and customer info
        project_reference_num: projectData.project_reference_num,
        customer_name: projectData.customer_name,
        customer_reference_number: projectData.customer_reference_number,
        branch: selectedBranch,
        
        // Asset info
        serial_number: asset.serial_number,
        tag_id: asset.tag_id,
        item_name: asset.item_name,
        category: asset.category,
        model: asset.model,
        status: asset.status,
        recipient_name: asset.recipient_name,
        department_name: asset.department_name,
        position: asset.position,
        
        // New fields
        windows: asset.windows || null,
        microsoft_office: asset.microsoft_office || null,
        software: selectedSoftware.length > 0 ? selectedSoftware : null, // Changed to array
        monthly_prices: asset.monthly_prices || null,
        
        // Convert antivirus dropdown to AV boolean
        av: asset.antivirus === '' ? null : (asset.antivirus.startsWith('Yes') ? 1 : 0),
        
        // Include only valid peripherals (those with both name and serial code)
        peripherals: peripherals.filter(p => p.peripheral_name.trim() && p.serial_code_name.trim())
      };
      
      console.log('Submitting asset data:', completeAssetData);
      
      const response = await apiService.createAssetWithDetails(completeAssetData);
      console.log('Asset created successfully:', response);
      
      if (response.success) {
        setAutoGenerated({
          asset_id: response.data.asset_id,
          pmid: response.data.pmid,
          inventory_id: response.data.inventory_id
        });
        toast.success('Asset created successfully!');
        setShowCreateConfirmation(false);
        
        // Navigate with state to trigger refresh on Assets page
        setTimeout(() => {
          navigate('/assets', { 
            state: { 
              refresh: true, 
              message: 'Asset created successfully',
              newAssetId: response.data.asset_id 
            },
            replace: false  // Use push instead of replace to ensure proper navigation
          });
        }, 2000);
      } else {
        setError(response.message || 'Failed to create asset');
        setLoading(false);
        setShowCreateConfirmation(false);
      }
      
    } catch (err) {
      console.error('Error creating asset:', err);
      setError(err.message || 'Failed to create asset');
      setLoading(false);
      setShowCreateConfirmation(false);
    }
  };

  const handleCancelClick = () => {
    setShowCancelConfirmation(true);
  };

  const handleConfirmCancel = () => {
    navigate('/assets');
  };

  const handleAssetChange = (e) => {
    setAsset({ ...asset, [e.target.name]: e.target.value });
  };

  const handleProjectRefChange = (e) => {
    const value = e.target.value;
    
    // Mark that user has interacted with the field
    if (!userHasInteracted && value.length > 0) {
      setUserHasInteracted(true);
    }
    
    setProjectSearchTerm(value);
    setProjectData(prev => ({
      ...prev,
      project_reference_num: value
    }));
    setShowProjectDropdown(value.length > 0);
    
    // Clear previous search errors when user types
    if (projectSearchError && value.length > 0) {
      setProjectSearchError(null);
    }
  };

  const handleProjectSelect = (project) => {
    setUserHasInteracted(true); // User has interacted by selecting
    setProjectSearchError(null); // Clear any errors
    
    // Set project data with fresh values (don't spread old projectData to avoid stale state)
    // Update project_reference_num first - this will trigger fetchProjectByReference via useEffect
    setProjectData({
      project_reference_num: project.Project_Ref_Number || '',
      customer_name: '', // EMPTY - let API fetch it
      customer_reference_number: '',
      project_title: ''
    });
    setProjectSearchTerm(project.Project_Ref_Number || '');
    setShowProjectDropdown(false);
    
    // Reset antivirus so user must select from the full dropdown with 3 choices
    setAsset(prev => ({
      ...prev,
      antivirus: ''
    }));
    
    // Trigger branch fetching if customer name is available
    if (project.Customer_Name) {
      fetchBranchesByCustomer(project.Customer_Name);
    }
  };

  const handleProjectInputFocus = () => {
    setUserHasInteracted(true); // Mark interaction when user focuses
    setShowProjectDropdown(true);
  };

  const handleProjectInputBlur = () => {
    // Delay hiding dropdown to allow for clicks
    setTimeout(() => setShowProjectDropdown(false), 300);
  };

  // Helper function to check if a peripheral form is complete
  const isPeripheralComplete = (peripheral) => {
    return peripheral.peripheral_name.trim() !== '' && peripheral.serial_code_name.trim() !== '';
  };

  // Helper function to check if we can add a new peripheral
  const canAddNewPeripheral = () => {
    if (peripherals.length === 0) return true;
    const lastPeripheral = peripherals[peripherals.length - 1];
    return isPeripheralComplete(lastPeripheral);
  };

  // Enhanced peripheral validation for submission
  const validatePeripherals = () => {
    const errors = [];
    peripherals.forEach((peripheral, index) => {
      if (peripheral.peripheral_name.trim() && !peripheral.serial_code_name.trim()) {
        errors.push(`Peripheral ${index + 1}: Serial code is required when peripheral name is provided`);
      }
    });
    return errors;
  };

  const handlePeripheralChange = (index, field, value) => {
    const updatedPeripherals = [...peripherals];
    updatedPeripherals[index][field] = value;
    setPeripherals(updatedPeripherals);
  };

  const addPeripheral = () => {
    // Only allow adding if current peripheral forms are complete
    if (canAddNewPeripheral()) {
      setPeripherals([...peripherals, {
        peripheral_name: '',
        serial_code_name: '',
        condition: 'Good',
        remarks: ''
      }]);
    }
  };

  const removePeripheral = (index) => {
    if (peripherals.length > 1) {
      const updatedPeripherals = peripherals.filter((_, i) => i !== index);
      setPeripherals(updatedPeripherals);
    }
  };

  // Software handlers (similar to branches in AddProject)
  const addSoftware = () => {
    if (softwareInput && !selectedSoftware.includes(softwareInput)) {
      setSelectedSoftware([...selectedSoftware, softwareInput]);
      setSoftwareInput('');
    }
  };

  const removeSoftware = (index) => {
    setSelectedSoftware(selectedSoftware.filter((_, i) => i !== index));
  };

  const handleSoftwareKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addSoftware();
    }
  };

  return (
    <div>
      <style>{`
        .dropdown-container {
          position: relative;
          z-index: 1000;
        }
        
        .dropdown-menu {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          background: white;
          border: 1px solid #ddd;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          max-height: 250px;
          overflow-y: auto;
          z-index: 1001;
          margin-top: 4px;
        }
        
        .dropdown-item {
          padding: 12px 16px;
          border-bottom: 1px solid #f0f0f0;
          cursor: pointer;
          transition: background-color 0.2s ease;
        }
        
        .dropdown-item:hover {
          background-color: #f8f9fa !important;
        }
        
        .dropdown-item:last-child {
          border-bottom: none;
        }
        
        .dropdown-ref-number {
          font-weight: 600;
          color: #333;
          margin-bottom: 4px;
        }
        
        .dropdown-title {
          font-size: 14px;
          color: #666;
          margin-bottom: 2px;
        }
        
        .dropdown-customer {
          font-size: 12px;
          color: #888;
        }
      `}</style>
      
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
        <button onClick={() => navigate('/assets')} className="btn btn-secondary" style={{ marginRight: '15px' }}>
          <ArrowLeft size={16} />
        </button>
        <h1>Add New Asset</h1>
      </div>

      {error && (
        <div style={{ 
          backgroundColor: '#f8d7da', 
          color: '#721c24', 
          padding: '15px', 
          borderRadius: '8px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <AlertCircle size={20} />
          {error}
        </div>
      )}

      {projectSearchError && userHasInteracted && (
        <div style={{ 
          backgroundColor: '#fff3cd', 
          color: '#856404', 
          padding: '12px', 
          borderRadius: '6px',
          marginBottom: '15px',
          border: '1px solid #ffeaa7',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <AlertCircle size={18} />
          {projectSearchError}
        </div>
      )}

      <div className="card">
        <form onSubmit={handleSubmitClick}>
          
          {/* Project Reference Section */}
          <div style={{ 
            backgroundColor: '#e8f5e8', 
            padding: '20px', 
            borderRadius: '8px', 
            marginBottom: '25px',
            border: '2px solid #4caf50'
          }}>
            <h3 style={{ 
              color: '#2e7d32', 
              marginBottom: '15px', 
              display: 'flex', 
              alignItems: 'center', 
              gap: '10px' 
            }}>
              <Hash size={20} />
              Step 1: Project Reference Number
            </h3>
            
            <div className="form-group">
              <label>Current Project Reference Number *</label>
              <div className="dropdown-container">
                <input
                  type="text"
                  value={projectSearchTerm}
                  onChange={handleProjectRefChange}
                  onFocus={handleProjectInputFocus}
                  onBlur={handleProjectInputBlur}
                  placeholder="Start typing project reference number..."
                  required
                  style={{ fontSize: '16px', fontWeight: 'bold', width: '100%' }}
                  autoComplete="off"
                />
                
                {showProjectDropdown && filteredProjects.length > 0 && (
                  <div className="dropdown-menu">
                    {filteredProjects.map((project, index) => (
                      <div
                        key={index}
                        className="dropdown-item"
                        onMouseDown={(e) => {
                          e.preventDefault(); // Prevent input blur
                          handleProjectSelect(project);
                        }}
                      >
                        <div className="dropdown-ref-number">
                          {project.Project_Ref_Number}
                        </div>
                        <div className="dropdown-title">
                          {project.Project_Title}
                        </div>
                        <div className="dropdown-customer">
                          Customer: {project.Customer_Name}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {showProjectDropdown && projectSearchTerm.length > 0 && filteredProjects.length === 0 && (
                  <div className="dropdown-menu">
                    <div style={{ padding: '12px', fontSize: '14px', color: '#666' }}>
                      No projects found matching "{projectSearchTerm}"
                    </div>
                  </div>
                )}
              </div>
              <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>
                Start typing to see available projects, or enter the exact project reference number
              </small>
            </div>

            {modalType === 'software' && (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: '#555', fontWeight: '600' }}>
                  Software Price (RM) <span style={{ color: '#f44336' }}>*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={newSoftwarePrice}
                  onChange={(e) => setNewSoftwarePrice(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !addingOption) {
                      handleAddNewOption();
                    }
                  }}
                  placeholder="e.g., 150.00"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                  disabled={addingOption}
                />
              </div>
            )}

            {/* Auto-populated Project Data (Read-only) */}
            {projectData.customer_name && (
              <div style={{ 
                backgroundColor: '#f0f8f0', 
                padding: '15px', 
                borderRadius: '6px', 
                marginTop: '15px',
                border: '1px solid #c8e6c9'
              }}>
                <h4 style={{ color: '#2e7d32', marginBottom: '10px' }}>Project Information:</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div>
                    <label style={{ fontWeight: 'bold', color: '#555' }}>Customer Name:</label>
                    <div style={{ padding: '8px', backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '4px' }}>
                      {projectData.customer_name}
                    </div>
                  </div>
                  <div>
                    <label style={{ fontWeight: 'bold', color: '#555' }}>Customer Reference:</label>
                    <div style={{ padding: '8px', backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '4px' }}>
                      {projectData.customer_reference_number}
                    </div>
                  </div>
                </div>
                {projectData.project_title && (
                  <div style={{ marginTop: '10px' }}>
                    <label style={{ fontWeight: 'bold', color: '#555' }}>Project Title:</label>
                    <div style={{ padding: '8px', backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '4px' }}>
                      {projectData.project_title}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Branch Selection Section */}
          {projectData.customer_name && (
            <div style={{ 
              backgroundColor: '#e3f2fd', 
              padding: '20px', 
              borderRadius: '8px', 
              marginBottom: '25px',
              border: '2px solid #2196f3'
            }}>
              <h3 style={{ 
                color: '#1976d2', 
                marginBottom: '15px', 
                display: 'flex', 
                alignItems: 'center', 
                gap: '10px' 
              }}>
                <MapPin size={20} />
                Step 2: Branch Selection
              </h3>
              
              <div className="form-group">
                <label>Select Branch *</label>
                
                {loading ? (
                  <div style={{ 
                    padding: '10px', 
                    backgroundColor: '#f5f5f5', 
                    border: '1px solid #ddd', 
                    borderRadius: '4px',
                    textAlign: 'center',
                    color: '#666',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}>
                    <Loader size={16} className="rotating-loader" />
                    Loading branches...
                  </div>
                ) : (
                  <select 
                    value={selectedBranch} 
                    onChange={(e) => setSelectedBranch(e.target.value)}
                    required
                    disabled={!projectData.customer_name || loading}
                    style={{ 
                      backgroundColor: !projectData.customer_name ? '#f5f5f5' : 'white',
                      opacity: !projectData.customer_name ? 0.7 : 1
                    }}
                  >
                    <option value="">
                      {branches.length === 0 
                        ? 'No branches available' 
                        : 'Select Branch'
                      }
                    </option>
                    {branches.map((branch, index) => (
                      <option key={index} value={branch.Branch}>
                        {branch.Branch}
                        {branch.Customer_ID && branch.Customer_ID.toString().startsWith('temp_') && ' (Default)'}
                        {branch.Customer_ID && branch.Customer_ID.toString().startsWith('fallback_') && ' (Fallback)'}
                      </option>
                    ))}
                  </select>
                )}
                
                {!projectData.customer_name && (
                  <small style={{ color: '#666', fontSize: '12px' }}>
                    Customer name must be populated first
                  </small>
                )}
                
                {branches.length > 0 && (
                  <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '5px' }}>
                    Found {branches.length} branch{branches.length !== 1 ? 'es' : ''} for {projectData.customer_name}
                  </small>
                )}
              </div>
            </div>
          )}

          {/* User-Provided Asset Data Section */}
          {selectedBranch && (
            <div style={{ 
              backgroundColor: '#f3e5f5', 
              padding: '20px', 
              borderRadius: '8px', 
              marginBottom: '25px',
              border: '2px solid #9c27b0'
            }}>
              <h3 style={{ 
                color: '#7b1fa2', 
                marginBottom: '15px', 
                display: 'flex', 
                alignItems: 'center', 
                gap: '10px' 
              }}>
                <Package size={20} />
                Step 3: Asset Information
              </h3>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div className="form-group">
                  <label>Serial Number *</label>
                  <input
                    type="text"
                    name="serial_number"
                    value={asset.serial_number}
                    onChange={handleAssetChange}
                    placeholder="78HT574"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Tag ID *</label>
                  <input
                    type="text"
                    name="tag_id"
                    value={asset.tag_id}
                    onChange={handleAssetChange}
                    placeholder="NADMA-A25001"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Item Name *</label>
                  <input
                    type="text"
                    name="item_name"
                    value={asset.item_name}
                    onChange={handleAssetChange}
                    placeholder="Komputer Meja (All-in-One)"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Status</label>
                  <select name="status" value={asset.status} onChange={handleAssetChange}>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Maintenance">Maintenance</option>
                    <option value="Retired">Retired</option>
                  </select>
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>
                      Category <span style={{ color: '#f44336' }}>*</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => handleOpenAddModal('category')}
                      style={{
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '5px 10px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        fontSize: '12px',
                        fontWeight: '600',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)'
                      }}
                      title="Add new Category"
                      onMouseOver={(e) => {
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.boxShadow = '0 3px 6px rgba(16, 185, 129, 0.4)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(16, 185, 129, 0.3)';
                      }}
                    >
                      <Plus size={14} /> 
                    </button>
                  </label>
                  <SearchableDropdown
                    options={categories.map(cat => ({
                      value: cat.Category || cat,
                      label: cat.Category || cat
                    }))}
                    value={asset.category}
                    onChange={(selectedValue) => setAsset({ ...asset, category: selectedValue || '' })}
                    placeholder="Type to search or select category..."
                    searchPlaceholder="Search categories..."
                    getOptionLabel={(option) => option.label}
                    getOptionValue={(option) => option.value}
                    required
                  />
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>
                      Model <span style={{ color: '#f44336' }}>*</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => handleOpenAddModal('model')}
                      style={{
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '5px 10px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        fontSize: '12px',
                        fontWeight: '600',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)'
                      }}
                      title="Add new Model"
                      onMouseOver={(e) => {
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.boxShadow = '0 3px 6px rgba(16, 185, 129, 0.4)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(16, 185, 129, 0.3)';
                      }}
                    >
                      <Plus size={14} /> 
                    </button>
                  </label>
                  <SearchableDropdown
                    options={models.map(mod => ({
                      value: mod.Model_Name || mod.Model || mod,
                      label: mod.Model_Name || mod.Model || mod
                    }))}
                    value={asset.model}
                    onChange={(selectedValue) => setAsset({ ...asset, model: selectedValue || '' })}
                    placeholder="Type to search or select model..."
                    searchPlaceholder="Search models..."
                    getOptionLabel={(option) => option.label}
                    getOptionValue={(option) => option.value}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Windows</label>
                  <input
                    type="text"
                    name="windows"
                    value={asset.windows}
                    onChange={handleAssetChange}
                    placeholder="e.g., Windows 10 Pro"
                  />
                </div>

                <div className="form-group">
                  <label>Microsoft Office</label>
                  <input
                    type="text"
                    name="microsoft_office"
                    value={asset.microsoft_office}
                    onChange={handleAssetChange}
                    placeholder="e.g., Microsoft Office 365"
                  />
                </div>

                <div className="form-group">
                  <label>Antivirus</label>
                  <select
                    value={asset.antivirus}
                    onChange={(e) => setAsset({ ...asset, antivirus: e.target.value })}
                  >
                    <option value="">Select Antivirus</option>
                    {projectAntivirusName && (
                      <option value={`Yes (${projectAntivirusName})`}>
                        Yes ({projectAntivirusName})
                      </option>
                    )}
                    <option value="No">No</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Monthly Price</label>
                  <input
                    type="number"
                    step="0.01"
                    name="monthly_prices"
                    value={asset.monthly_prices}
                    onChange={handleAssetChange}
                    placeholder="e.g., 150.00"
                  />
                </div>

                <div
                  style={{
                    gridColumn: '1 / -1',
                    borderTop: '1px solid #e0e0e0',
                    margin: '8px 6px 12px 6px'
                  }}
                />

                <div className="form-group">
                  <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>Software</span>
                    <button
                      type="button"
                      onClick={() => handleOpenAddModal('software')}
                      style={{
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '5px 10px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        fontSize: '12px',
                        fontWeight: '600',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)'
                      }}
                      title="Add new Software"
                      onMouseOver={(e) => {
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.boxShadow = '0 3px 6px rgba(16, 185, 129, 0.4)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(16, 185, 129, 0.3)';
                      }}
                    >
                      <Plus size={14} />
                    </button>
                  </label>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <SearchableDropdown
                      options={softwareOptions.map(opt => {
                        const name = opt.Software_Name || opt.name || opt;
                        const price = opt.Price ?? opt.price;
                        return {
                          value: name,
                          label: name,
                          price
                        };
                      })}
                      value={softwareInput}
                      onChange={(selectedValue) => {
                        setSoftwareInput(selectedValue || '');
                      }}
                      placeholder="Type to search or select software..."
                      searchPlaceholder="Search software..."
                      getOptionLabel={(option) => option.label}
                      getOptionValue={(option) => option.value}
                      renderOption={(option) => (
                        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                          <span>{option.label}</span>
                          {option.price != null && option.price !== '' && (
                            <span style={{ color: '#555' }}>RM {option.price}</span>
                          )}
                        </div>
                      )}
                      style={{ flex: 1 }}
                    />
                    <button 
                      type="button" 
                      onClick={addSoftware}
                      className="btn btn-secondary"
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      Add Software
                    </button>
                  </div>
                  
                  {/* Display selected software */}
                  {selectedSoftware.length > 0 && (
                    <div style={{ 
                      marginTop: '15px', 
                      display: 'flex', 
                      flexWrap: 'wrap', 
                      gap: '10px' 
                    }}>
                      {selectedSoftware.map((software, index) => {
                        const softwareOption = softwareOptions.find(opt => 
                          (opt.Software_Name || opt.name || opt) === software
                        );
                        const price = softwareOption?.Price ?? softwareOption?.price;
                        
                        return (
                          <div 
                            key={index}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '8px 12px',
                              backgroundColor: '#e3f2fd',
                              border: '1px solid #2196F3',
                              borderRadius: '20px',
                              fontSize: '14px'
                            }}
                          >
                            <Package size={14} color="#2196F3" />
                            <span>{software}</span>
                            {price != null && price !== '' && (
                              <span style={{ color: '#666', fontSize: '12px' }}>
                                (RM {price})
                              </span>
                            )}
                            <button
                              type="button"
                              onClick={() => removeSoftware(index)}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '2px',
                                display: 'flex',
                                alignItems: 'center'
                              }}
                            >
                              <Trash2 size={16} color="#666" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Recipient Information Section */}
          {selectedBranch && (
            <div style={{ 
              backgroundColor: '#fff3e0', 
              padding: '20px', 
              borderRadius: '8px', 
              marginBottom: '25px',
              border: '2px solid #ff9800'
            }}>
              <h3 style={{ 
                color: '#f57c00', 
                marginBottom: '15px', 
                display: 'flex', 
                alignItems: 'center', 
                gap: '10px' 
              }}>
                <User size={20} />
                Step 4: Recipient Information
              </h3>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div className="form-group">
                  <label>Recipient Name *</label>
                  <input
                    type="text"
                    name="recipient_name"
                    value={asset.recipient_name}
                    onChange={handleAssetChange}
                    placeholder="Insp Abdullah bin Ahmad Sobri"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Department Name *</label>
                  <input
                    type="text"
                    name="department_name"
                    value={asset.department_name}
                    onChange={handleAssetChange}
                    placeholder="Ketua SHU C (ETK) Ajutan"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Position</label>
                  <input
                    type="text"
                    name="position"
                    value={asset.position}
                    onChange={handleAssetChange}
                    placeholder="Manager, Officer"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Peripheral Section */}
          {selectedBranch && (
            <div style={{ 
              backgroundColor: '#e0f2f1', 
              padding: '20px', 
              borderRadius: '8px', 
              marginBottom: '25px',
              border: '2px solid #009688'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h3 style={{ 
                  color: '#00695c', 
                  margin: 0,
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '10px' 
                }}>
                  <Wrench size={20} />
                  Step 5: Peripherals (Optional)
                </h3>
                <button 
                  type="button" 
                  onClick={addPeripheral} 
                  className={`btn ${canAddNewPeripheral() ? 'btn-secondary' : 'btn-disabled'}`}
                  disabled={!canAddNewPeripheral()}
                  style={{ 
                    padding: '8px 12px', 
                    fontSize: '14px',
                    opacity: canAddNewPeripheral() ? 1 : 0.5,
                    cursor: canAddNewPeripheral() ? 'pointer' : 'not-allowed'
                  }}
                  title={!canAddNewPeripheral() ? 'Complete the current peripheral form before adding another' : 'Add a new peripheral'}
                >
                  <Plus size={14} style={{ marginRight: '5px' }} />
                  Add Peripheral
                </button>
              </div>

              {!canAddNewPeripheral() && peripherals.length > 0 && (
                <div style={{
                  backgroundColor: '#fff3cd',
                  color: '#856404',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  marginBottom: '15px',
                  border: '1px solid #ffeaa7',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <Lightbulb size={16} />
                  Complete the current peripheral form (name and serial code required) before adding another
                </div>
              )}
              
              {peripherals.map((peripheral, index) => (
                <div key={index} style={{ 
                  backgroundColor: '#f0f8f0', 
                  padding: '15px', 
                  borderRadius: '6px', 
                  marginBottom: '15px',
                  border: '1px solid #b2dfdb'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <h4 style={{ color: '#00695c', margin: 0 }}>Peripheral {index + 1}</h4>
                    {peripherals.length > 1 && (
                      <button 
                        type="button" 
                        onClick={() => removePeripheral(index)}
                        style={{ 
                          background: 'none', 
                          border: 'none', 
                          color: '#f44336', 
                          cursor: 'pointer',
                          padding: '5px'
                        }}
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                    <div className="form-group" style={{ marginBottom: '10px' }}>
                      <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>
                          Peripheral Name
                          {peripheral.peripheral_name.trim() !== '' && <span style={{ color: '#4caf50' }}> ✓</span>}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleOpenAddModal('peripheral')}
                          style={{
                            background: '#4caf50',
                            color: 'white',
                            border: 'none',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          <Plus size={14} />
                        </button>
                      </label>
                      <select
                        value={peripheral.peripheral_name}
                        onChange={(e) => handlePeripheralChange(index, 'peripheral_name', e.target.value)}
                        className={peripheral.peripheral_name.trim() && !peripheral.serial_code_name.trim() ? 'peripheral-warning' : ''}
                      >
                        <option value="">Select peripheral type</option>
                        {peripheralTypes.map((type, idx) => {
                          const typeValue = type.Peripheral_Type_Name || type;
                          return (
                            <option key={`ptype-${typeValue}-${idx}`} value={typeValue}>
                              {typeValue}
                            </option>
                          );
                        })}
                      </select>
                    </div>

                    <div className="form-group" style={{ marginBottom: '10px' }}>
                      <label>
                        Serial Code
                        {peripheral.peripheral_name.trim() !== '' && (
                          <span style={{ color: '#f44336' }}> * (Required)</span>
                        )}
                        {peripheral.serial_code_name.trim() !== '' && <span style={{ color: '#4caf50' }}> ✓</span>}
                      </label>
                      <input
                        type="text"
                        value={peripheral.serial_code_name}
                        onChange={(e) => handlePeripheralChange(index, 'serial_code_name', e.target.value)}
                        placeholder="CN-GE90-9865"
                        style={{
                          borderColor: peripheral.peripheral_name.trim() && !peripheral.serial_code_name.trim() ? '#ff9800' : ''
                        }}
                      />
                      {peripheral.peripheral_name.trim() && !peripheral.serial_code_name.trim() && (
                        <small style={{ color: '#ff9800', fontSize: '11px', display: 'block', marginTop: '2px' }}>
                          Serial code is required when peripheral name is selected
                        </small>
                      )}
                    </div>

                    <div className="form-group" style={{ marginBottom: '10px' }}>
                      <label>Condition</label>
                      <select 
                        value={peripheral.condition} 
                        onChange={(e) => handlePeripheralChange(index, 'condition', e.target.value)}
                      >
                        <option value="Good">Good</option>
                        <option value="Fair">Fair</option>
                        <option value="Poor">Poor</option>
                        <option value="Needs Repair">Needs Repair</option>
                      </select>
                    </div>

                    <div className="form-group" style={{ marginBottom: '10px' }}>
                      <label>Remarks</label>
                      <input
                        type="text"
                        value={peripheral.remarks}
                        onChange={(e) => handlePeripheralChange(index, 'remarks', e.target.value)}
                        placeholder="Optional remarks"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}



          <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={loading || !selectedBranch}
              style={{ 
                padding: '12px 24px',
                fontSize: '16px',
                fontWeight: 'bold'
              }}
            >
              <Save size={16} style={{ marginRight: '8px' }} />
              {loading ? 'Creating Asset...' : 'Create Asset'}
            </button>
            <button 
              type="button" 
              onClick={handleCancelClick}
              className="btn btn-secondary"
              disabled={loading}
              style={{ 
                padding: '12px 24px',
                fontSize: '16px'
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>

      {/* Create Asset Confirmation Modal */}
      <ConfirmationModal
        isOpen={showCreateConfirmation}
        onClose={() => setShowCreateConfirmation(false)}
        onConfirm={handleConfirmCreate}
        title="Create New Asset"
        message="Are you sure you want to create this new asset? Please review the information below before confirming."
        confirmText="Create Asset"
        cancelText="Review"
        type="success"
        showSummary={true}
        summaryData={{
          projectRef: projectData.project_reference_num,
          customer: projectData.customer_name,
          branch: selectedBranch,
          serialNumber: asset.serial_number,
          tagId: asset.tag_id,
          itemName: asset.item_name,
          category: asset.category,
          model: asset.model,
          peripheralCount: peripherals.filter(p => p.peripheral_name.trim() && p.serial_code_name.trim()).length
        }}
        loading={loading}
      />

      {/* Cancel Confirmation Modal */}
      <ConfirmationModal
        isOpen={showCancelConfirmation}
        onClose={() => setShowCancelConfirmation(false)}
        onConfirm={handleConfirmCancel}
        title="Cancel Asset Creation"
        message="Are you sure you want to cancel? All entered data will be lost and cannot be recovered."
        confirmText="Yes, Cancel"
        cancelText="Continue Editing"
        type="danger"
      />

      {/* Add New Option Modal */}
      {showAddModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}
          onClick={handleCloseAddModal}
        >
          <div 
            style={{
              backgroundColor: 'white',
              padding: '25px',
              borderRadius: '8px',
              minWidth: '400px',
              maxWidth: '500px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, color: '#333' }}>
                Add New {
                  modalType === 'category' ? 'Category' : 
                  modalType === 'model' ? 'Model' : 
                  modalType === 'peripheral' ? 'Peripheral Type' :
                  modalType === 'windows' ? 'Windows Version' :
                  modalType === 'office' ? 'Office Version' :
                  modalType === 'antivirus' ? 'Antivirus' :
                  modalType === 'software' ? 'Software' : ''
                }
              </h3>
              <button
                onClick={handleCloseAddModal}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '0',
                  lineHeight: '1'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: '#555', fontWeight: '500' }}>
                {
                  modalType === 'category' ? 'Category' : 
                  modalType === 'model' ? 'Model' : 
                  modalType === 'peripheral' ? 'Peripheral Type' :
                  modalType === 'windows' ? 'Windows Version' :
                  modalType === 'office' ? 'Office Version' :
                  modalType === 'antivirus' ? 'Antivirus' :
                  modalType === 'software' ? 'Software' : ''
                } Name
              </label>
              <input
                type="text"
                value={newOptionValue}
                onChange={(e) => setNewOptionValue(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !addingOption) {
                    handleAddNewOption();
                  }
                }}
                placeholder={
                  modalType === 'category' ? 'e.g., Desktop, Laptop, Server' : 
                  modalType === 'model' ? 'e.g., Dell OptiPlex 7420, HP EliteBook' : 
                  modalType === 'peripheral' ? 'e.g., Monitor, Keyboard, Mouse' :
                  modalType === 'windows' ? 'e.g., Windows 10 Pro, Windows 11' :
                  modalType === 'office' ? 'e.g., Office 2019, Microsoft 365' :
                  modalType === 'antivirus' ? 'e.g., Kaspersky, Norton, McAfee' :
                  modalType === 'software' ? 'e.g., Adobe Acrobat, AutoCAD' : ''
                }
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
                autoFocus
                disabled={addingOption}
              />
            </div>

            {modalType === 'software' && (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: '#555', fontWeight: '600' }}>
                  Software Price (RM) <span style={{ color: '#f44336' }}>*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={newSoftwarePrice}
                  onChange={(e) => setNewSoftwarePrice(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !addingOption) {
                      handleAddNewOption();
                    }
                  }}
                  placeholder="e.g., 150.00"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                  disabled={addingOption}
                />
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={handleCloseAddModal}
                disabled={addingOption}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  color: '#666',
                  cursor: addingOption ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  opacity: addingOption ? 0.5 : 1
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddNewOption}
                disabled={addingOption || !newOptionValue.trim() || (modalType === 'software' && !newSoftwarePrice.trim())}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: addingOption || !newOptionValue.trim() || (modalType === 'software' && !newSoftwarePrice.trim()) ? '#ccc' : '#4caf50',
                  color: 'white',
                  cursor: addingOption || !newOptionValue.trim() ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px'
                }}
              >
                {addingOption ? 'Adding...' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddAsset;
